import { auth } from './firebase';

// WebSocket server configuration
// The realtime server runs WebSocket on /ws endpoint
const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8080/ws';

export interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

export type MessageHandler = (message: WebSocketMessage) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 5000;
  private reconnectTimer: number | null = null;
  private isIntentionalClose = false;
  private subscriptions: Set<string> = new Set();
  private authenticated = false;
  private userId: string | null = null;

  /**
   * Connect to WebSocket server with Firebase authentication
   */
  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('✅ Already connected to WebSocket');
      return;
    }

    try {
      // Get Firebase token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      console.log('🔑 Got Firebase token, connecting to WebSocket...');

      // Connect with token in query parameter (Method 1 - Recommended)
      const wsUrl = `${WS_URL}?token=${token}`;
      this.ws = new WebSocket(wsUrl);

      this.setupEventHandlers();
    } catch (error) {
      console.error('❌ Error connecting to WebSocket:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('✅ WebSocket connected');
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('❌ Error parsing WebSocket message:', error);
      }
    };

    this.ws.onclose = (event) => {
      console.log(`🔌 WebSocket disconnected (code: ${event.code}, reason: ${event.reason})`);
      this.authenticated = false;
      this.userId = null;

      if (!this.isIntentionalClose) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    console.log('📨 WebSocket message:', message.type, message);

    switch (message.type) {
      case 'connected':
        console.log('🎉 Connected to Momentum WebSocket Server');
        if (message.authenticated) {
          this.authenticated = true;
          this.userId = message.userId;
          console.log('✅ Authenticated as:', message.userId);
          this.resubscribeToChannels();
        }
        break;

      case 'authenticated':
        this.authenticated = true;
        this.userId = message.userId;
        console.log('✅ Authenticated:', message.email);
        this.resubscribeToChannels();
        break;

      case 'auth_error':
        console.error('❌ Authentication failed:', message.message);
        break;

      case 'subscribed':
        console.log('✅ Subscribed to:', message.channel);
        break;

      case 'unsubscribed':
        console.log('✅ Unsubscribed from:', message.channel);
        break;

      case 'pong':
        // Keep-alive response
        break;

      default:
        // Broadcast message to all handlers
        this.messageHandlers.forEach(handler => handler(message));
    }
  }

  /**
   * Subscribe to a channel
   */
  subscribe(channel: string): void {
    if (!this.isConnected()) {
      console.warn('⚠️ Not connected, storing subscription for later:', channel);
      this.subscriptions.add(channel);
      return;
    }

    if (!this.authenticated) {
      console.warn('⚠️ Not authenticated, storing subscription for later:', channel);
      this.subscriptions.add(channel);
      return;
    }

    this.subscriptions.add(channel);
    this.send({
      type: 'subscribe',
      channel
    });
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channel: string): void {
    this.subscriptions.delete(channel);

    if (this.isConnected() && this.authenticated) {
      this.send({
        type: 'unsubscribe',
        channel
      });
    }
  }

  /**
   * Resubscribe to all channels after reconnection
   */
  private resubscribeToChannels(): void {
    if (!this.authenticated) return;

    console.log('🔄 Resubscribing to channels:', Array.from(this.subscriptions));
    this.subscriptions.forEach(channel => {
      this.send({
        type: 'subscribe',
        channel
      });
    });
  }

  /**
   * Send a message to the WebSocket server
   */
  send(message: WebSocketMessage): void {
    if (!this.isConnected()) {
      console.warn('⚠️ Cannot send message, not connected');
      return;
    }

    try {
      this.ws!.send(JSON.stringify(message));
    } catch (error) {
      console.error('❌ Error sending message:', error);
    }
  }

  /**
   * Add a message handler
   */
  addMessageHandler(handler: MessageHandler): void {
    this.messageHandlers.add(handler);
  }

  /**
   * Remove a message handler
   */
  removeMessageHandler(handler: MessageHandler): void {
    this.messageHandlers.delete(handler);
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Check if authenticated
   */
  isAuthenticated(): boolean {
    return this.authenticated;
  }

  /**
   * Get user ID
   */
  getUserId(): string | null {
    return this.userId;
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;

    console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Send ping to keep connection alive
   */
  ping(): void {
    if (this.isConnected()) {
      this.send({ type: 'ping' });
    }
  }

  /**
   * Start keep-alive timer
   */
  startKeepAlive(interval: number = 30000): void {
    setInterval(() => {
      this.ping();
    }, interval);
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.isIntentionalClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.authenticated = false;
    this.userId = null;
    this.subscriptions.clear();
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
